import { FormEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Alert,
  Button,
  Dialog,
  Field,
  Heading,
  Input,
  Label,
  Paragraph,
  Select,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import { Team } from '../../api/queries';
import { useUpdateTeamSettingsMutation } from '../../hooks/mutations/useUpdateTeamSettingsMutation';
import { useDeleteTeamMutation } from '../../hooks/mutations/useDeleteTeamMutation';
import { createTeamFormSchema } from '../TeamForm/TeamForm.schema';
import { SettingsSection } from '../TeamSettingsLayout';
import classes from './TeamCardEdit.module.css';

type TeamCardEditProps = {
  teamName: Team['name'];
  numberOfTeamMembers: Team['numberOfTeamMembers'];
  workLanguage?: Team['workLanguage'];
  teamId: Team['id'];
};

type WorkLanguageOption = 'NB' | 'EN' | 'inherit';

export const TeamCardEdit = ({
  teamName,
  numberOfTeamMembers,
  workLanguage,
  teamId,
}: TeamCardEditProps) => {
  const { t } = useTranslation();
  const teamFormSchema = useMemo(() => createTeamFormSchema(t), [t]);
  const navigate = useNavigate();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [name, setName] = useState(teamName);
  const [members, setMembers] = useState(String(numberOfTeamMembers));
  const [language, setLanguage] = useState<WorkLanguageOption>(workLanguage ?? 'inherit');

  const { mutate: updateTeamSettings, isPending: isSaving } =
    useUpdateTeamSettingsMutation(teamId);
  const { mutate: deleteTeam, isPending: isDeleting } = useDeleteTeamMutation();

  const clearErrors = () => {
    setSaveError(null);
    setFieldErrors({});
  };

  const onSave = (event: FormEvent) => {
    event.preventDefault();
    clearErrors();

    const result = teamFormSchema.safeParse({ name, numberOfTeamMembers: members });
    if (!result.success) {
      const errors: Record<string, string> = {};
      result.error.errors.forEach((issue) => {
        const field = String(issue.path[0]);
        errors[field] ??= issue.message;
      });
      setFieldErrors(errors);
      return;
    }

    updateTeamSettings(
      {
        teamName: result.data.name,
        numberOfTeamMembers: result.data.numberOfTeamMembers,
        workLanguage: language === 'inherit' ? null : language,
      },
      {
        onSuccess: () => {
          toast.success(t('teamCardEdit.toast.saveSuccess'), { position: 'bottom-right' });
        },
        onError: (error: any) => {
          setSaveError(
            error?.response?.data?.message || t('teamCardEdit.errors.saveError.defaultMessage'),
          );
        },
      },
    );
  };

  const onDeleteTeam = () => {
    deleteTeam(teamId, {
      onSuccess: () => {
        toast.success(t('teamCardEdit.toast.deleteSuccess'), { position: 'bottom-right' });
        navigate('/teams');
      },
    });
  };

  return (
    <>
      <SettingsSection title={t('teamSettings.about.title')} description={t('teamSettings.about.description')}>
        <form className={classes.form} onSubmit={onSave} noValidate>
          <div className={classes.fieldRow}>
            <Field>
              <Label htmlFor="team-name">{t('teamSettings.about.teamName')}</Label>
              <Input
                id="team-name"
                name="teamName"
                value={name}
                aria-invalid={fieldErrors.name ? true : undefined}
                onChange={(e) => {
                  clearErrors();
                  setName(e.target.value);
                }}
              />
              {fieldErrors.name && <ValidationMessage>{fieldErrors.name}</ValidationMessage>}
            </Field>
            <Field>
              <Label htmlFor="team-members">{t('teamSettings.about.members')}</Label>
              <Input
                id="team-members"
                name="numberOfTeamMembers"
                type="number"
                inputMode="numeric"
                min={3}
                className={classes.membersInput}
                value={members}
                aria-invalid={fieldErrors.numberOfTeamMembers ? true : undefined}
                onChange={(e) => {
                  clearErrors();
                  setMembers(e.target.value);
                }}
              />
              {fieldErrors.numberOfTeamMembers && (
                <ValidationMessage>{fieldErrors.numberOfTeamMembers}</ValidationMessage>
              )}
            </Field>
          </div>

          <Field className={classes.languageField}>
            <Label htmlFor="team-language">{t('teamSettings.about.language')}</Label>
            <Paragraph data-size="sm" id="team-language-description" className={classes.subtle}>
              {t('teamSettings.about.languageDescription')}
            </Paragraph>
            <Select
              id="team-language"
              name="workLanguage"
              aria-describedby="team-language-description"
              value={language}
              onChange={(e) => {
                clearErrors();
                setLanguage(e.target.value as WorkLanguageOption);
              }}
            >
              <Select.Option value="inherit">{t('teamSettings.about.languageOptions.inherit')}</Select.Option>
              <Select.Option value="NB">{t('teamSettings.about.languageOptions.norwegian')}</Select.Option>
              <Select.Option value="EN">{t('teamSettings.about.languageOptions.english')}</Select.Option>
            </Select>
          </Field>

          {saveError && (
            <Alert data-color="danger">
              <Heading level={3} data-size="2xs" className={classes.alertHeading}>
                {t('teamCardEdit.errors.saveError.title')}
              </Heading>
              <Paragraph>{saveError}</Paragraph>
            </Alert>
          )}

          <div>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? t('teamSettings.common.saving') : t('teamSettings.common.save')}
            </Button>
          </div>
        </form>
      </SettingsSection>

      <SettingsSection
        variant="danger"
        headingId="delete-team-heading"
        title={t('teamSettings.delete.title')}
        description={t('teamSettings.delete.description')}
        action={
          <Button variant="secondary" data-color="danger" onClick={() => setIsDeleteOpen(true)}>
            {t('teamSettings.delete.button')}
          </Button>
        }
      />

      <Dialog
        open={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        closedby="any"
        closeButton={false}
        aria-labelledby="delete-team-dialog-heading"
        className={classes.dialog}
      >
        <div className={classes.dialogContent}>
          <Heading id="delete-team-dialog-heading" level={2} data-size="xs">
            {t('teamSettings.delete.dialogTitle', { name: teamName })}
          </Heading>
          <Paragraph>{t('teamSettings.delete.description')}</Paragraph>
          <div className={classes.dialogActions}>
            <Button data-color="danger" onClick={onDeleteTeam} disabled={isDeleting}>
              {t('teamSettings.delete.button')}
            </Button>
            <Button variant="tertiary" onClick={() => setIsDeleteOpen(false)}>
              {t('teamSettings.common.cancel')}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};
